import { useCallback, useEffect, useMemo, useState } from "react";
import { BellRing, CreditCard, Hand, MonitorSmartphone, ShoppingBag, SlidersHorizontal, Volume2, VolumeX } from "lucide-react";
import { PageLoader } from "../../component/common/Loader";
import { toast } from "../../Utills/toast";
import {
  normalizeTerminalSoundSettings,
  readTerminalSoundPreference,
  TERMINAL_SOUND_CHANGED_EVENT,
  TERMINAL_SOUND_TEST_EVENT,
  terminalSoundStorageKey,
  writeTerminalSoundPreference,
} from "../../Utills/terminalSoundPreference";
import { getDeviceSettings, saveNotificationSound } from "./deviceSettingsApi";
import { getGeneralSettings, saveGeneralSettings } from "./generalSettingsApi";
import "./GeneralSettings.css";

const soundOptions = [
  {
    key: "newOrderEnabled",
    title: "New orders",
    description: "Play a sound for new dine-in and takeaway orders.",
    icon: ShoppingBag,
  },
  {
    key: "waiterRequestEnabled",
    title: "Waiter requests",
    description: "Play a sound when a guest requests staff assistance.",
    icon: Hand,
  },
  {
    key: "paymentEnabled",
    title: "Payment confirmations",
    description: "Play a sound when a dine-in or takeaway payment is confirmed.",
    icon: CreditCard,
  },
];

const GeneralSettings = () => {
  const [settings, setSettings] = useState(null);
  const [billingSettings, setBillingSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const identity = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("admin_token_info") || "{}"); } catch (_) { return {}; }
  }, []);
  const canManage = [1, 2].includes(Number(identity.roleId)) ||
    ["admin", "manager"].includes(String(identity.role || "").toLowerCase());

  const load = useCallback(async () => {
    try {
      const [result, general] = await Promise.all([getDeviceSettings(), getGeneralSettings()]);
      const next = normalizeTerminalSoundSettings(result?.terminal || {});
      setSettings(next);
      setBillingSettings({ requirePaymentMethod: general?.requirePaymentMethod !== false, reservationDepositRequired: Boolean(general?.reservationDepositRequired), reservationDepositType: general?.reservationDepositType === "Percentage" ? "Percentage" : "Fixed", reservationDepositValue: Number(general?.reservationDepositValue) || 0, reservationReminderEnabled: Boolean(general?.reservationReminderEnabled), reservationReminderHours: Number(general?.reservationReminderHours) || 24, reservationReminderChannel: general?.reservationReminderChannel === "WhatsApp" ? "WhatsApp" : "SMS", reservationNoShowAutomationEnabled: Boolean(general?.reservationNoShowAutomationEnabled), reservationNoShowGraceMinutes: Number(general?.reservationNoShowGraceMinutes) || 30 });
      writeTerminalSoundPreference(identity.restaurantId, next);
    } catch (error) { toast.error(error.message); }
  }, [identity.restaurantId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const restaurantId = String(identity.restaurantId || "").toLowerCase();
    const onChanged = event => {
      if (event.detail?.restaurantId === restaurantId) {
        setSettings(normalizeTerminalSoundSettings(event.detail.settings));
      }
    };
    const onStorage = event => {
      if (event.key === terminalSoundStorageKey(identity.restaurantId)) {
        setSettings(readTerminalSoundPreference(identity.restaurantId));
      }
    };
    window.addEventListener(TERMINAL_SOUND_CHANGED_EVENT, onChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(TERMINAL_SOUND_CHANGED_EVENT, onChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [identity.restaurantId]);

  const save = async next => {
    if (!canManage || busy) return;
    const previous = settings;
    setSettings(next);
    setBusy(true);
    try {
      const terminal = await saveNotificationSound(next);
      const saved = normalizeTerminalSoundSettings(terminal || next);
      setSettings(saved);
      writeTerminalSoundPreference(identity.restaurantId, saved, true);
      toast.success("Sound preferences saved for this device.");
    } catch (error) {
      setSettings(previous);
      toast.error(error.message);
    } finally { setBusy(false); }
  };

  const testSound = () => window.dispatchEvent(new CustomEvent(TERMINAL_SOUND_TEST_EVENT, {
    detail: { onResult: success => success
      ? toast.success("Test sound played.")
      : toast.error("Sound could not play. Check this device's volume and browser permission.") }
  }));

  const savePaymentRequirement = async checked => {
    if (!canManage || busy) return;
    const previous = billingSettings;
    const next = { ...billingSettings, requirePaymentMethod: checked };
    setBillingSettings(next);
    setBusy(true);
    try {
      const saved = await saveGeneralSettings(next);
      setBillingSettings({ ...next, ...saved });
      toast.success("Payment requirement saved for this restaurant.");
    } catch (error) {
      setBillingSettings(previous);
      toast.error(error.message);
    } finally { setBusy(false); }
  };
  const saveReservationSetting = async patch => {
    if (!canManage || busy) return;
    const previous = billingSettings;
    const next = { ...billingSettings, ...patch };
    setBillingSettings(next); setBusy(true);
    try { const saved = await saveGeneralSettings(next); setBillingSettings({ ...next, ...saved }); toast.success("Reservation settings saved."); }
    catch (error) { setBillingSettings(previous); toast.error(error.message); }
    finally { setBusy(false); }
  };

  if (!settings || !billingSettings) return <PageLoader label="Loading general settings..." />;

  return <main className="general-settings-page">
    <header className="general-settings-header">
      <span className="general-settings-heading-icon" aria-hidden="true"><SlidersHorizontal /></span>
      <div className="general-settings-heading-copy">
        <span>GENERAL SETTINGS</span>
        <h1>General settings</h1>
        <p>Configure restaurant-wide billing behavior and choose which alerts should make a sound on this device.</p>
      </div>
    </header>

    <section className="general-settings-card general-payment-card">
      <div className="general-master-row">
        <span className={`general-master-icon ${billingSettings.requirePaymentMethod ? "is-on" : ""}`}>
          <CreditCard />
        </span>
        <div className="general-setting-copy">
          <h2>Require payment method</h2>
          <p>Ask staff to choose Cash, Card, UPI or another method before marking any bill as paid.</p>
          <small className="general-setting-scope">Restaurant-wide · Table, Menu, Bill and Takeaway</small>
        </div>
        <label className="general-toggle">
          <input type="checkbox" aria-label="Require payment method before marking a bill as paid"
            checked={billingSettings.requirePaymentMethod} disabled={busy || !canManage}
            onChange={event => savePaymentRequirement(event.target.checked)} />
          <span className="general-toggle-track" aria-hidden="true"><span /></span>
        </label>
      </div>
    </section>

    <section className="general-settings-card general-payment-card">
      <div className="general-master-row">
        <span className={`general-master-icon ${billingSettings.reservationDepositRequired ? "is-on" : ""}`}><CreditCard /></span>
        <div className="general-setting-copy"><h2>Reservation deposit</h2><p>Require an advance amount when staff create a reservation.</p><small className="general-setting-scope">Restaurant-wide · Reservations</small></div>
        <label className="general-toggle"><input type="checkbox" checked={billingSettings.reservationDepositRequired} disabled={busy || !canManage} onChange={event => saveReservationSetting({ reservationDepositRequired: event.target.checked })} /><span className="general-toggle-track" aria-hidden="true"><span /></span></label>
      </div>
      <div className={`general-sound-options ${billingSettings.reservationDepositRequired ? "" : "is-muted"}`}>
        <div className="general-option-row"><div className="general-setting-copy"><h3>Deposit rule</h3><p>Choose a fixed advance or a percentage of the booking amount.</p></div><select value={billingSettings.reservationDepositType} disabled={busy || !canManage || !billingSettings.reservationDepositRequired} onChange={event => saveReservationSetting({ reservationDepositType: event.target.value })}><option value="Fixed">Fixed amount</option><option value="Percentage">Percentage</option></select><input type="number" min="0" value={billingSettings.reservationDepositValue} disabled={busy || !canManage || !billingSettings.reservationDepositRequired} onChange={event => saveReservationSetting({ reservationDepositValue: event.target.value })} aria-label="Reservation deposit value" /></div>
      </div>
      <div className="general-master-row"><span className={`general-master-icon ${billingSettings.reservationReminderEnabled ? "is-on" : ""}`}><BellRing /></span><div className="general-setting-copy"><h3>Reservation reminders</h3><p>Keep the reminder schedule and delivery history ready for your messaging provider.</p></div><label className="general-toggle"><input type="checkbox" checked={billingSettings.reservationReminderEnabled} disabled={busy || !canManage} onChange={event => saveReservationSetting({ reservationReminderEnabled: event.target.checked })} /><span className="general-toggle-track" aria-hidden="true"><span /></span></label></div>
      <div className={`general-sound-options ${billingSettings.reservationReminderEnabled ? "" : "is-muted"}`}><div className="general-option-row"><div className="general-setting-copy"><h3>Send reminder before</h3><p>Choose how many hours before the booking the reminder is scheduled.</p></div><select value={billingSettings.reservationReminderHours} disabled={busy || !canManage || !billingSettings.reservationReminderEnabled} onChange={event => saveReservationSetting({ reservationReminderHours: event.target.value })}><option value="1">1 hour</option><option value="2">2 hours</option><option value="6">6 hours</option><option value="12">12 hours</option><option value="24">24 hours</option><option value="48">48 hours</option></select></div><div className="general-option-row"><div className="general-setting-copy"><h3>Reminder channel</h3><p>Send each reservation reminder through SMS or WhatsApp.</p></div><select value={billingSettings.reservationReminderChannel} disabled={busy || !canManage || !billingSettings.reservationReminderEnabled} onChange={event => saveReservationSetting({ reservationReminderChannel: event.target.value })}><option value="SMS">SMS</option><option value="WhatsApp">WhatsApp</option></select></div></div>
      <div className="general-master-row"><span className={`general-master-icon ${billingSettings.reservationNoShowAutomationEnabled ? "is-on" : ""}`}><BellRing /></span><div className="general-setting-copy"><h3>Automatic no-show</h3><p>Mark a guest as no-show when they have not checked in after the selected grace period.</p></div><label className="general-toggle"><input type="checkbox" checked={billingSettings.reservationNoShowAutomationEnabled} disabled={busy || !canManage} onChange={event => saveReservationSetting({ reservationNoShowAutomationEnabled: event.target.checked })} /><span className="general-toggle-track" aria-hidden="true"><span /></span></label></div>
      <div className={`general-sound-options ${billingSettings.reservationNoShowAutomationEnabled ? "" : "is-muted"}`}><div className="general-option-row"><div className="general-setting-copy"><h3>No-show grace period</h3><p>Give staff this much time after the reservation before marking it as no-show.</p></div><select value={billingSettings.reservationNoShowGraceMinutes} disabled={busy || !canManage || !billingSettings.reservationNoShowAutomationEnabled} onChange={event => saveReservationSetting({ reservationNoShowGraceMinutes: event.target.value })}><option value="15">15 minutes</option><option value="30">30 minutes</option><option value="45">45 minutes</option><option value="60">60 minutes</option><option value="90">90 minutes</option></select></div></div>
    </section>

    <section className="general-settings-card">
      <div className="general-master-row">
        <span className={`general-master-icon ${settings.enabled ? "is-on" : ""}`}>
          {settings.enabled ? <Volume2 /> : <VolumeX />}
        </span>
        <div className="general-setting-copy">
          <h2>Sound alerts</h2>
          <p>Master sound control for this browser and device.</p>
        </div>
        <label className="general-toggle">
          <input type="checkbox" aria-label="Enable sound alerts on this device" checked={settings.enabled}
            disabled={busy || !canManage} onChange={event => save({ ...settings, enabled: event.target.checked })} />
          <span className="general-toggle-track" aria-hidden="true"><span /></span>
        </label>
      </div>

      <div className={`general-sound-options ${settings.enabled ? "" : "is-muted"}`}>
        {soundOptions.map(option => {
          const Icon = option.icon;
          return <div className="general-option-row" key={option.key}>
            <span className="general-option-icon"><Icon /></span>
            <div className="general-setting-copy"><h3>{option.title}</h3><p>{option.description}</p></div>
            <label className="general-toggle">
              <input type="checkbox" aria-label={`Enable ${option.title.toLowerCase()} sound`} checked={settings[option.key]}
                disabled={busy || !canManage || !settings.enabled}
                onChange={event => save({ ...settings, [option.key]: event.target.checked })} />
              <span className="general-toggle-track" aria-hidden="true"><span /></span>
            </label>
          </div>;
        })}
      </div>

      <footer className="general-settings-footer">
        <div><MonitorSmartphone /><span>{canManage
          ? "These choices apply only to this device."
          : "Only an Admin or Manager can change sound preferences."}</span></div>
        <button type="button" onClick={testSound} disabled={busy || !settings.enabled}>
          <BellRing size={18} /> Test sound
        </button>
      </footer>
    </section>
  </main>;
};

export default GeneralSettings;
